import React, { useState, useEffect, useRef, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import itLocale from '@fullcalendar/core/locales/it';
import {
  Card,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Badge,
  Alert,
  Spinner,
  Table
} from 'react-bootstrap';
import moment from 'moment';
import 'moment/locale/it';
import { bookingService } from '../../services/api';
import MobileDayStrip from '../Planner/MobileDayStrip';
import './AthleteBookingSection.css';

const AthleteBookingSection = () => {
  const calendarRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Courses & Filters
  const [myCourses, setMyCourses] = useState([]);
  const [selectedCourseFilter, setSelectedCourseFilter] = useState('');
  const [lastBookedCourseId, setLastBookedCourseId] = useState(null);

  // Slots & Events
  const [events, setEvents] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: moment().startOf('week').format('YYYY-MM-DD'),
    end: moment().endOf('week').format('YYYY-MM-DD')
  });

  // Mobile selected date
  const [selectedDate, setSelectedDate] = useState(new Date());

  // My Bookings
  const [myBookings, setMyBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  // Booking Modal
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedCourseForBooking, setSelectedCourseForBooking] = useState('');
  const [submittingBooking, setSubmittingBooking] = useState(false);

  // Cancellation Modal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [submittingCancel, setSubmittingCancel] = useState(false);

  // Alerts
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const showNotification = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback({ type: '', message: '' }), 6000);
  };

  // Track window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (calendarRef.current) {
        const api = calendarRef.current.getApi();
        if (mobile && api.view.type !== 'timeGridDay') {
          api.changeView('timeGridDay');
        } else if (!mobile && api.view.type === 'timeGridDay') {
          api.changeView('timeGridWeek');
        }
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 1. Carica i corsi dell'atleta e le sue prenotazioni per calcolare il default del corso
  const loadInitialData = useCallback(async () => {
    setLoadingBookings(true);
    try {
      const [coursesRes, bookingsRes] = await Promise.all([
        bookingService.getMyCourses(),
        bookingService.getMyBookings()
      ]);

      const coursesList = coursesRes?.success && Array.isArray(coursesRes?.data) ? coursesRes.data : [];
      setMyCourses(coursesList);

      const bookingsList = bookingsRes?.success && Array.isArray(bookingsRes?.data) ? bookingsRes.data : [];
      setMyBookings(bookingsList);

      // Default corso: ultimo corso prenotato, se esiste, altrimenti il primo corso attivo
      let defaultCourseId = '';
      if (bookingsList.length > 0) {
        const lastWithCourse = bookingsList.find((b) => b.courseId);
        if (lastWithCourse) {
          defaultCourseId = lastWithCourse.courseId;
          setLastBookedCourseId(lastWithCourse.courseId);
        }
      }
      if (!defaultCourseId && coursesList.length > 0) {
        defaultCourseId = coursesList[0].courseId;
      }
      setSelectedCourseFilter(defaultCourseId);
    } catch (err) {
      console.error('Error loading initial booking data:', err);
    } finally {
      setLoadingBookings(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // 2. Carica gli slot disponibili per l'intervallo visibile
  const loadSlots = useCallback(async (startStr, endStr, courseId) => {
    setLoadingSlots(true);
    const res = await bookingService.getAvailableSlots({
      startDate: startStr,
      endDate: endStr,
      courseId: courseId || undefined
    });
    setLoadingSlots(false);

    if (res?.success && Array.isArray(res?.data)) {

      // Mappa gli slot in eventi FullCalendar
      const calEvents = res.data.map((slot) => {
        let bgColor = '#198754'; // Verde: disponibile
        let borderColor = '#146c43';
        let statusText = 'Disponibile';

        if (slot.isBookedByCurrentUser) {
          bgColor = '#6f42c1'; // Viola: mia prenotazione
          borderColor = '#59359a';
          statusText = 'Tua Prenotazione';
        } else if (slot.isFull) {
          bgColor = '#6c757d'; // Grigio: al completo
          borderColor = '#565e64';
          statusText = 'Completo';
        } else if (slot.remainingSpots !== null && slot.remainingSpots <= 2) {
          bgColor = '#fd7e14'; // Arancio: quasi pieno
          borderColor = '#d66708';
          statusText = `${slot.remainingSpots} ${slot.remainingSpots === 1 ? 'posto' : 'posti'}`;
        } else if (slot.remainingSpots !== null) {
          statusText = `${slot.remainingSpots} posti`;
        }

        const titleParts = [];
        if (slot.title) titleParts.push(slot.title);
        else if (slot.courseName) titleParts.push(slot.courseName);
        else titleParts.push('Lezione Libera');

        titleParts.push(`(${statusText})`);

        return {
          id: slot.id,
          title: titleParts.join(' '),
          start: `${slot.date}T${slot.startTime}:00`,
          end: `${slot.date}T${slot.endTime}:00`,
          backgroundColor: bgColor,
          borderColor: borderColor,
          textColor: '#ffffff',
          extendedProps: {
            ...slot,
            statusText
          }
        };
      });

      setEvents(calEvents);
    } else {
      showNotification('danger', res.message || 'Errore nel caricamento degli slot');
    }
  }, []);

  // Aggiorna slot al cambio del filtro corso o dell'intervallo visibile
  useEffect(() => {
    if (dateRange.start && dateRange.end) {
      loadSlots(dateRange.start, dateRange.end, selectedCourseFilter);
    }
  }, [dateRange, selectedCourseFilter, loadSlots]);

  // Gestione cambio date FullCalendar (settimana/giorno)
  const handleDatesSet = (dateInfo) => {
    const startStr = moment(dateInfo.start).format('YYYY-MM-DD');
    const endStr = moment(dateInfo.end).subtract(1, 'day').format('YYYY-MM-DD');
    setDateRange({ start: startStr, end: endStr });
  };

  // Click su un evento slot del calendario
  const handleEventClick = (clickInfo) => {
    const slotProps = clickInfo.event.extendedProps;

    if (slotProps.isBookedByCurrentUser) {
      // Slot già prenotato dall'atleta -> apri modale di cancellazione
      const booking = myBookings.find((b) => b.id === slotProps.currentUserBookingId || b.bookingSlotId === slotProps.id);
      if (booking) {
        setBookingToCancel(booking);
        setShowCancelModal(true);
      } else {
        // Fallback minimale per cancellazione
        setBookingToCancel({
          id: slotProps.currentUserBookingId,
          date: slotProps.date,
          startTime: slotProps.startTime,
          endTime: slotProps.endTime,
          courseName: slotProps.courseName,
          canCancel: true
        });
        setShowCancelModal(true);
      }
      return;
    }

    if (slotProps.isFull) {
      showNotification('warning', 'Questo slot ha esaurito i posti disponibili.');
      return;
    }

    // Slot disponibile -> apri modale di prenotazione
    setSelectedSlot(slotProps);

    // Imposta default del corso
    if (slotProps.courseId) {
      setSelectedCourseForBooking(slotProps.courseId);
    } else {
      // Slot generico: usa il filtro selezionato o l'ultimo corso prenotato
      const defaultCourse = selectedCourseFilter || lastBookedCourseId || (myCourses[0]?.courseId || '');
      setSelectedCourseForBooking(defaultCourse);
    }

    setShowBookingModal(true);
  };

  // Conferma prenotazione
  const handleConfirmBooking = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return;

    // Se lo slot non ha corso predefinito, è obbligatorio aver scelto un corso
    if (!selectedSlot.courseId && !selectedCourseForBooking) {
      showNotification('warning', 'È obbligatorio selezionare il corso desiderato.');
      return;
    }

    // Risolvi subscription associata al corso
    const targetCourseId = selectedSlot.courseId || selectedCourseForBooking;
    const selectedSub = myCourses.find((c) => c.courseId === targetCourseId);

    setSubmittingBooking(true);
    const res = await bookingService.bookSlot({
      bookingSlotId: selectedSlot.id,
      courseSubscriptionId: selectedSub?.courseSubscriptionId || undefined,
      courseId: targetCourseId
    });
    setSubmittingBooking(false);

    if (res?.success) {
      showNotification('success', 'Prenotazione confermata con successo! Riceverai un\'email riassuntiva.');
      setShowBookingModal(false);
      // Ricarica slot e prenotazioni
      loadInitialData();
      if (dateRange.start && dateRange.end) {
        loadSlots(dateRange.start, dateRange.end, selectedCourseFilter);
      }
    } else {
      showNotification('danger', res.message || 'Errore durante la prenotazione');
    }
  };

  // Conferma cancellazione
  const handleConfirmCancel = async () => {
    if (!bookingToCancel) return;

    setSubmittingCancel(true);
    const res = await bookingService.cancelBooking(bookingToCancel.id);
    setSubmittingCancel(false);

    if (res?.success) {
      showNotification('success', 'Prenotazione cancellata con successo.');
      setShowCancelModal(false);
      setBookingToCancel(null);
      loadInitialData();
      if (dateRange.start && dateRange.end) {
        loadSlots(dateRange.start, dateRange.end, selectedCourseFilter);
      }
    } else {
      showNotification('danger', res.message || 'Errore durante la cancellazione');
    }
  };

  // Selezione data per strip mobile
  const handleSelectDateMobile = (date) => {
    setSelectedDate(date);
    if (calendarRef.current) {
      const api = calendarRef.current.getApi();
      api.gotoDate(date);
    }
  };

  // Dettagli del corso selezionato per la prenotazione (mostra ingressi se a consumo)
  const currentSelectedCourseDetail = myCourses.find(
    (c) => c.courseId === (selectedSlot?.courseId || selectedCourseForBooking)
  );

  return (
    <Card className="mb-4 shadow-sm border-0 athlete-booking-card">
      <Card.Header className="bg-primary text-white d-flex flex-wrap justify-content-between align-items-center py-3">
        <div className="d-flex align-items-center mb-2 mb-md-0">
          <i className="fas fa-calendar-plus fa-lg me-2"></i>
          <div>
            <h5 className="mb-0 fw-bold">Prenota Lezioni & Slot Disponibili</h5>
            <small className="text-white-50">
              Scegli una fascia oraria e prenota in autonomia la tua sessione
            </small>
          </div>
        </div>

        {/* Filtro per corso */}
        <div className="d-flex align-items-center">
          <Form.Label className="text-white mb-0 me-2 small fw-semibold text-nowrap">
            <i className="fas fa-graduation-cap me-1"></i> Mostra corso:
          </Form.Label>
          <Form.Select
            size="sm"
            className="w-auto border-0 shadow-sm"
            value={selectedCourseFilter}
            onChange={(e) => setSelectedCourseFilter(e.target.value)}
          >
            <option value="">Tutti i corsi / Lezioni libere</option>
            {myCourses.map((c) => (
              <option key={c.courseId} value={c.courseId}>
                {c.courseName}{' '}
                {c.offerType === 'pay_per_use'
                  ? `(${c.remainingEntries ?? 0} ingr. rimasti)`
                  : '(Iscritto)'}
              </option>
            ))}
          </Form.Select>
        </div>
      </Card.Header>

      <Card.Body className="p-3 p-md-4">
        {feedback.message && (
          <Alert
            variant={feedback.type}
            dismissible
            onClose={() => setFeedback({ type: '', message: '' })}
            className="mb-3"
          >
            {feedback.message}
          </Alert>
        )}

        {/* Legenda Colori */}
        <div className="d-flex flex-wrap gap-2 align-items-center mb-3 p-2 bg-light rounded border">
          <span className="small fw-semibold text-muted me-2">Legenda:</span>
          <span className="badge bg-success">
            <i className="fas fa-circle me-1"></i> Posti Disponibili
          </span>
          <span className="badge bg-warning text-dark">
            <i className="fas fa-exclamation-triangle me-1"></i> In Esaurimento (≤ 2 posti)
          </span>
          <span className="badge" style={{ backgroundColor: '#6f42c1', color: 'white' }}>
            <i className="fas fa-check-circle me-1"></i> La Tua Prenotazione
          </span>
          <span className="badge bg-secondary">
            <i className="fas fa-lock me-1"></i> Al Completo
          </span>
          <span className="small text-muted ms-auto fst-italic">
            Clicca su uno slot per prenotare o cancellare
          </span>
        </div>

        {/* Mobile Day Strip */}
        {isMobile && (
          <MobileDayStrip selectedDate={selectedDate} onSelectDate={handleSelectDateMobile} />
        )}

        {/* Loading Indicator */}
        {loadingSlots && (
          <div className="text-center py-2">
            <Spinner animation="border" size="sm" variant="primary" className="me-2" />
            <small className="text-muted">Aggiornamento disponibilità...</small>
          </div>
        )}

        {/* FullCalendar Component */}
        <div className="athlete-calendar-wrapper mb-4">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
            initialView={isMobile ? 'timeGridDay' : 'timeGridWeek'}
            locale={itLocale}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: isMobile ? '' : 'timeGridWeek,timeGridDay'
            }}
            slotMinTime="07:00:00"
            slotMaxTime="23:00:00"
            allDaySlot={false}
            events={events}
            eventClick={handleEventClick}
            datesSet={handleDatesSet}
            height="auto"
            nowIndicator={true}
            slotDuration="00:30:00"
            expandRows={true}
          />
        </div>

        {/* Sezione: Le Mie Prenotazioni Attive */}
        <div className="mt-4 pt-3 border-top">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="fw-bold mb-0">
              <i className="fas fa-ticket-alt text-primary me-2"></i>
              Le Mie Prenotazioni ({myBookings.length})
            </h6>
            <Button variant="outline-secondary" size="sm" onClick={loadInitialData}>
              <i className="fas fa-sync me-1"></i> Aggiorna
            </Button>
          </div>

          {loadingBookings ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" variant="primary" />
            </div>
          ) : myBookings.length === 0 ? (
            <Alert variant="light" className="border text-center py-4 mb-0">
              <i className="fas fa-calendar-times fa-2x text-muted mb-2"></i>
              <p className="mb-0 text-muted">Non hai ancora effettuato alcuna prenotazione attiva.</p>
              <small className="text-muted">Clicca su uno slot verde nel calendario sopra per prenotare!</small>
            </Alert>
          ) : (
            <Table responsive hover className="align-middle mb-0">
              <thead className="table-light small">
                <tr>
                  <th>Data & Orario</th>
                  <th>Corso</th>
                  <th>Istruttore</th>
                  <th>Luogo</th>
                  <th>Stato</th>
                  <th className="text-end">Azione</th>
                </tr>
              </thead>
              <tbody>
                {myBookings.map((b) => (
                  <tr key={b.id} className={b.status === 'cancelled' ? 'table-light text-muted' : ''}>
                    <td>
                      <strong>{moment(b.date).format('DD/MM/YYYY')}</strong>
                      <div className="small text-muted">
                        {b.startTime} - {b.endTime}
                      </div>
                    </td>
                    <td>
                      <Badge bg="primary">{b.courseName || 'Corso'}</Badge>
                    </td>
                    <td>{b.coachName}</td>
                    <td>{b.locationName}</td>
                    <td>
                      <Badge bg={b.status === 'confirmed' ? 'success' : 'danger'}>
                        {b.status === 'confirmed' ? 'Confermata' : 'Cancellata'}
                      </Badge>
                    </td>
                    <td className="text-end">
                      {b.status === 'confirmed' && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={!b.canCancel}
                          onClick={() => {
                            setBookingToCancel(b);
                            setShowCancelModal(true);
                          }}
                          title={!b.canCancel ? 'Termine cancellazione superato' : 'Cancella prenotazione'}
                        >
                          <i className="fas fa-times me-1"></i> Cancella
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Card.Body>

      {/* MODALE DI CONFERMA PRENOTAZIONE */}
      <Modal show={showBookingModal} onHide={() => setShowBookingModal(false)} centered>
        <Form onSubmit={handleConfirmBooking}>
          <Modal.Header closeButton className="bg-light">
            <Modal.Title className="fw-bold h5">
              <i className="fas fa-calendar-check text-success me-2"></i>
              Conferma la tua Prenotazione
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {selectedSlot && (
              <>
                <div className="bg-light p-3 rounded mb-3 border">
                  <Row className="g-2">
                    <Col xs={6}>
                      <span className="text-muted small">Data:</span>
                      <div className="fw-bold">{moment(selectedSlot.date).format('dddd, DD MMMM YYYY')}</div>
                    </Col>
                    <Col xs={6}>
                      <span className="text-muted small">Orario:</span>
                      <div className="fw-bold">
                        {selectedSlot.startTime} - {selectedSlot.endTime}
                      </div>
                    </Col>
                    <Col xs={6}>
                      <span className="text-muted small">Istruttore:</span>
                      <div>{selectedSlot.coachName}</div>
                    </Col>
                    <Col xs={6}>
                      <span className="text-muted small">Luogo:</span>
                      <div>{selectedSlot.locationName}</div>
                    </Col>
                    {selectedSlot.remainingSpots !== null && (
                      <Col xs={12} className="pt-2 border-top">
                        <span className="text-muted small">Disponibilità residua:</span>{' '}
                        <Badge bg={selectedSlot.remainingSpots <= 2 ? 'warning' : 'success'}>
                          {selectedSlot.remainingSpots} {selectedSlot.remainingSpots === 1 ? 'posto rimasto' : 'posti rimasti'}
                        </Badge>
                      </Col>
                    )}
                  </Row>
                </div>

                {/* Selezione Corso */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    Corso Desiderato <span className="text-danger">*</span>
                  </Form.Label>
                  {selectedSlot.courseId ? (
                    <div>
                      <Badge bg="info" className="text-dark p-2 fs-6">
                        <i className="fas fa-book me-1"></i> {selectedSlot.courseName}
                      </Badge>
                      <Form.Text className="d-block text-muted small mt-1">
                        Questo slot è dedicato specificamente a {selectedSlot.courseName}.
                      </Form.Text>
                    </div>
                  ) : (
                    <div>
                      <Form.Select
                        value={selectedCourseForBooking}
                        onChange={(e) => setSelectedCourseForBooking(e.target.value)}
                        required
                      >
                        <option value="">-- Seleziona il corso --</option>
                        {myCourses.map((c) => (
                          <option key={c.courseId} value={c.courseId}>
                            {c.courseName}{' '}
                            {c.offerType === 'pay_per_use'
                              ? `(${c.remainingEntries ?? 0} ingressi disponibili)`
                              : ''}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted small">
                        Slot libero: scegli obbligatoriamente a quale dei tuoi corsi attivi imputare la lezione.
                      </Form.Text>
                    </div>
                  )}
                </Form.Group>

                {/* Info abbonamento a ingressi */}
                {currentSelectedCourseDetail?.offerType === 'pay_per_use' && (
                  <Alert variant="info" className="py-2 px-3 small mb-3">
                    <i className="fas fa-info-circle me-1"></i>
                    Abbonamento a ingressi: hai{' '}
                    <strong>{currentSelectedCourseDetail.remainingEntries ?? 0}</strong> ingressi
                    residui. L&apos;ingresso verrà scalato solo al momento dell&apos;appello.
                  </Alert>
                )}

                <p className="text-muted small mb-0">
                  <i className="fas fa-bell me-1"></i>
                  La prenotazione è confermata immediatamente. Potrai cancellarla dal tuo portale nel rispetto dei tempi minimi di anticipo.
                </p>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowBookingModal(false)} disabled={submittingBooking}>
              Annulla
            </Button>
            <Button variant="success" type="submit" disabled={submittingBooking}>
              {submittingBooking ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Conferma in corso...
                </>
              ) : (
                <>
                  <i className="fas fa-check me-1"></i> Conferma Prenotazione
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODALE DI CANCELLAZIONE PRENOTAZIONE */}
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-bold h5 text-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Cancella Prenotazione
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {bookingToCancel && (
            <>
              <p>Confermi di voler cancellare la tua prenotazione per:</p>
              <div className="bg-light p-3 rounded mb-3 border">
                <div>
                  <strong>Corso:</strong> {bookingToCancel.courseName || 'Corso'}
                </div>
                <div>
                  <strong>Data:</strong> {moment(bookingToCancel.date).format('dddd, DD MMMM YYYY')}
                </div>
                <div>
                  <strong>Orario:</strong> {bookingToCancel.startTime} - {bookingToCancel.endTime}
                </div>
              </div>
              <p className="text-muted small mb-0">
                Il posto tornerà immediatamente disponibile per altri atleti e riceverai un&apos;email di conferma cancellazione.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)} disabled={submittingCancel}>
            Indietro
          </Button>
          <Button variant="danger" onClick={handleConfirmCancel} disabled={submittingCancel}>
            {submittingCancel ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Cancellazione in corso...
              </>
            ) : (
              <>
                <i className="fas fa-trash me-1"></i> Conferma Cancellazione
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default AthleteBookingSection;
